/**
 * HCCChartChasingPage.jsx
 *
 * 4-panel HCC chart review UI. Single screen, no router dependency.
 *
 * Public folder expected layout:
 *   /public/data/chart_codes_consolidated.json
 *   /public/data/encounter_trace_{encounter_id}.json   (one per encounter)
 *   /public/pages/{n}.pdf                              (single-page PDFs)
 *   /public/pages/{n}.txt                              (OCR text per page)
 *
 * Falls back to embedded DEMO_* data when files are not found.
 *
 * Dependencies: react, antd, @ant-design/icons
 * Chat uses Anthropic /v1/messages (key injected server-side via proxy).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography, Button, Tag, Tooltip, Spin, Modal,
  Input, Select, message, Badge, Divider, Empty, Form,
} from 'antd';
import {
  CheckOutlined, CloseOutlined, QuestionCircleOutlined,
  DownOutlined, RightOutlined, PlusOutlined, SendOutlined,
  FileTextOutlined, MedicineBoxOutlined, CheckCircleFilled,
  SyncOutlined, SaveOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ─── Design tokens ──────────────────────────────────────────────────────────
const C = {
  brand:        '#2a4a7c',
  brandDark:    '#162349',
  brandLight:   '#BDD8E5',
  gospel:       '#15803D',
  gospelBg:     '#F0FDF4',
  gospelBdr:    '#BBF7D0',
  supported:    '#1D4ED8',
  supportedBg:  '#EFF6FF',
  supportedBdr: '#BFDBFE',
  indirect:     '#92400E',
  indirectBg:   '#FFFBEB',
  indirectBdr:  '#FDE68A',
  approved:     '#15803D',
  rejected:     '#B91C1C',
  hccV28:       '#1E3A5F',
  hccV24:       '#374151',
  hccRx:        '#701A75',
  border:       '#E5E7EB',
  HEADER_H:     52,
};

// ─── Pure helpers ────────────────────────────────────────────────────────────
const supportMeta = (ss, ds) => {
  if (ss === 'gospel')
    return { label: 'Direct — A&P', color: C.gospel,    bg: C.gospelBg,    border: C.gospelBdr };
  if (ds === 'indirect-supported')
    return { label: 'Indirect',     color: C.indirect,  bg: C.indirectBg,  border: C.indirectBdr };
  return   { label: 'Corroborated', color: C.supported, bg: C.supportedBg, border: C.supportedBdr };
};

const hccBadges = (m) => {
  if (!m?.mapped) return [];
  const b = [];
  if (m.hcc_v28   === 'Yes') b.push({ label: 'HCC V28', color: C.hccV28 });
  if (m.hcc_v24   === 'Yes') b.push({ label: 'HCC V24', color: C.hccV24 });
  if (m.rxhcc_v08 === 'Yes') b.push({ label: 'RxHCC',   color: C.hccRx });
  return b;
};

// ─── Pill ────────────────────────────────────────────────────────────────────
const Pill = ({ label, color, bg, border }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '1px 7px', borderRadius: 9999,
    fontSize: 17, fontWeight: 600, letterSpacing: '0.03em',
    color, background: bg || color + '18',
    border: `1px solid ${border || color + '44'}`,
    whiteSpace: 'nowrap',
  }}>{label}</span>
);

// ─── OCR Evidence snippet ────────────────────────────────────────────────────
const EvidenceSnippet = ({ context, section }) => {
  if (!context?.match) return null;
  return (
    <div style={{
      fontFamily: 'monospace', fontSize: 16,
      background: '#FAFAFA', color: '#374151',
      borderRadius: 5, padding: '7px 10px',
      lineHeight: 1.7, marginTop: 4,
      border: '1px solid #E5E7EB',
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>
      <span style={{ color: '#9CA3AF' }}>{context.before}</span>
      <mark style={{ background: '#FDE047', color: '#1C1917', borderRadius: 2, padding: '0 2px', fontWeight: 700 }}>
        {context.match}
      </mark>
      <span style={{ color: '#9CA3AF' }}>{context.after}</span>
      {section && <div style={{ marginTop: 3, color: C.brand, fontSize: 16 }}>§ {section}</div>}
    </div>
  );
};

// ─── CodeRow ─────────────────────────────────────────────────────────────────
const CodeRow = ({ finding, isSelected, isExpanded, decision, onSelect, onToggleExpand, onApprove, onReject, onPageJump }) => {
  const sm     = supportMeta(finding.support_status, finding.display_status);
  const badges = hccBadges(finding.hcc_mapping);
  const hasAdj = finding.rule_modifications?.length > 0;
  const pEv    = finding.evidence?.[0];
  const corrEv = finding.corroboration?.supporting_evidence || [];

  const statusIcon =
    decision === 'approved' ? <CheckCircleFilled style={{ color: C.approved, fontSize: 16 }} /> :
    decision === 'rejected' ? <CloseOutlined     style={{ color: C.rejected, fontSize: 16 }} /> :
                              <QuestionCircleOutlined style={{ color: '#9CA3AF', fontSize: 16 }} />;

  const bg   = isSelected ? '#EDE9FE' : decision === 'approved' ? '#F0FDF4' : decision === 'rejected' ? '#FFF1F0' : '#fff';
  const bleft= isSelected ? `3px solid ${C.brand}` : decision === 'approved' ? `3px solid ${C.approved}` : decision === 'rejected' ? `3px solid ${C.rejected}` : '3px solid transparent';

  return (
    <div style={{ background: bg, borderLeft: bleft, borderBottom: `1px solid ${C.border}`, opacity: decision === 'rejected' ? 0.58 : 1, transition: 'all 0.12s' }}>

      {/* main row */}
      <div onClick={onSelect} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 10px', cursor: 'pointer' }}>
        <div style={{ paddingTop: 2, flexShrink: 0 }}>{statusIcon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
            <Text style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 17, color: '#111827' }}>
              {finding.icd10_code}
            </Text>
            {badges.map(b => (
              <span key={b.label} style={{ fontSize: 16, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: b.color, color: '#fff', letterSpacing: '0.04em' }}>
                {b.label}
              </span>
            ))}
            {hasAdj && (
              <Tooltip title={finding.rule_modifications[0]?.rule_reasoning}>
                <SyncOutlined style={{ color: C.brand, fontSize: 16 }} />
              </Tooltip>
            )}
          </div>
          <Text style={{ fontSize: 17, color: '#374151', display: 'block', lineHeight: '17px', marginTop: 1 }}>
            {finding.description}
          </Text>
          <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
            <Pill label={sm.label} color={sm.color} bg={sm.bg} border={sm.border} />
            <Pill label={finding.source === 'physician' ? '👨‍⚕️ Physician' : finding.source === 'coder' ? '✏️ Coder' : '🤖 AI'} color='#374151' bg='#F3F4F6' border='#D1D5DB' />
            {finding.meat_category && <Pill label={finding.meat_category} color='#1D4ED8' bg='#EFF6FF' border='#BFDBFE' />}
          </div>
        </div>
        <div onClick={e => { e.stopPropagation(); onToggleExpand(); }} style={{ flexShrink: 0, color: '#9CA3AF', padding: '2px 0', cursor: 'pointer' }}>
          {isExpanded ? <DownOutlined style={{ fontSize: 17 }} /> : <RightOutlined style={{ fontSize: 17 }} />}
        </div>
      </div>

      {/* expanded provenance */}
      {isExpanded && (
        <div style={{ padding: '0 10px 10px 33px' }}>
          {pEv && (
            <>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2 }}>
                Chart Evidence
                {pEv.page && (
                  <button onClick={() => onPageJump(pEv.page)} style={{ marginLeft: 8, fontSize: 17, color: C.brand, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600, padding: 0 }}>
                    → Page {pEv.page}
                  </button>
                )}
              </div>
              <EvidenceSnippet context={pEv.context} section={pEv.section} />
            </>
          )}

          {finding.corroboration?.llm_reasoning && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2 }}>AI Reasoning</div>
              <div style={{ fontSize: 16.5, color: '#374151', background: '#F9FAFB', border: `1px solid ${C.border}`, borderRadius: 4, padding: '6px 9px', lineHeight: 1.6 }}>
                {finding.corroboration.llm_reasoning}
              </div>
            </div>
          )}

          {corrEv.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>
                Supporting Evidence ({corrEv.length})
              </div>
              {corrEv.slice(0, 4).map((ev, i) => (
                <div key={i} style={{ fontSize: 16, color: '#374151', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 4, padding: '4px 9px', marginBottom: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 17 }}>{ev.context?.match || ev.text}</span>
                  {ev.page && (
                    <button onClick={() => onPageJump(ev.page)} style={{ fontSize: 17, color: C.brand, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontWeight: 600, flexShrink: 0, marginLeft: 6 }}>
                      p.{ev.page}
                    </button>
                  )}
                </div>
              ))}
              {corrEv.length > 4 && <Text style={{ fontSize: 16, color: '#9CA3AF' }}>+{corrEv.length - 4} more</Text>}
            </div>
          )}

          {hasAdj && (
            <div style={{ marginTop: 8, padding: '5px 8px', background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 5, fontSize: 17 }}>
              <SyncOutlined style={{ color: C.brand, marginRight: 5 }} />
              <Text style={{ color: C.brand }}>
                Code adjusted: <strong>{finding.rule_modifications[0]?.before_code}</strong> → <strong>{finding.rule_modifications[0]?.after_code}</strong>
                {' — '}{finding.rule_modifications[0]?.rule_reasoning}
              </Text>
            </div>
          )}
        </div>
      )}

      {/* action bar */}
      {isSelected && decision === 'pending' && (
        <div style={{ display: 'flex', gap: 8, padding: '7px 10px 9px 33px', borderTop: `1px solid ${C.border}` }}>
          <Button size="small" type="primary" icon={<CheckOutlined />} onClick={onApprove}
            style={{ background: C.approved, borderColor: C.approved, fontSize: 17 }}>Approve</Button>
          <Button size="small" danger icon={<CloseOutlined />} onClick={onReject}
            style={{ fontSize: 17 }}>Reject</Button>
        </div>
      )}
      {isSelected && decision !== 'pending' && (
        <div style={{ display: 'flex', gap: 8, padding: '5px 10px 7px 33px', borderTop: `1px solid ${C.border}` }}>
          {decision === 'approved' && (
            <Button size="small" danger icon={<CloseOutlined />} onClick={onReject} style={{ fontSize: 16 }}>Undo — Reject</Button>
          )}
          {decision === 'rejected' && (
            <Button size="small" icon={<CheckOutlined />} onClick={onApprove}
              style={{ fontSize: 16, color: C.approved, borderColor: C.approved }}>Undo — Approve</Button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Add Code Modal ──────────────────────────────────────────────────────────
const AddCodeModal = ({ visible, encounterDate, onAdd, onClose }) => {
  const [form] = Form.useForm();
  return (
    <Modal
      open={visible}
      title={<span style={{ color: C.brand, fontWeight: 700 }}><PlusOutlined style={{ marginRight: 8 }} />Add Missed Code — {encounterDate}</span>}
      onCancel={onClose}
      width={540}
      footer={[
        <Button key="c" onClick={onClose}>Cancel</Button>,
        <Button key="a" type="primary" style={{ background: C.brand }}
          onClick={() => form.validateFields().then(v => { onAdd(v); form.resetFields(); })}>
          Add &amp; Approve
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item name="icd10_code" label="ICD-10 Code" style={{ flex: '0 0 140px' }} rules={[{ required: true }]}>
            <Input placeholder="e.g. E11.40" style={{ fontFamily: 'monospace', fontWeight: 700 }} />
          </Form.Item>
          <Form.Item name="description" label="Description" style={{ flex: 1 }} rules={[{ required: true }]}>
            <Input placeholder="Diagnosis description" />
          </Form.Item>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item name="meat_category" label="MEAT / Section" style={{ flex: 1 }} rules={[{ required: true }]}>
            <Select placeholder="Where documented?">
              <Option value="A&P">A&amp;P — Assessment &amp; Plan</Option>
              <Option value="Problem List">Problem List</Option>
              <Option value="Medications">Medications</Option>
              <Option value="Labs/Results">Labs / Results</Option>
              <Option value="Vitals">Vitals</Option>
              <Option value="HPI">HPI</Option>
              <Option value="Other">Other</Option>
            </Select>
          </Form.Item>
          <Form.Item name="page_ref" label="Chart Page #" style={{ flex: '0 0 110px' }}>
            <Input placeholder="Page no." type="number" />
          </Form.Item>
        </div>
        <Form.Item name="coder_reasoning" label="Coder Provenance" rules={[{ required: true, message: 'Please describe where and why this code is supported.' }]}>
          <TextArea rows={3} placeholder="Describe the supporting documentation and why this code should be captured…" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Chat Panel ───────────────────────────────────────────────────────────────
const ChatPanel = ({ encounterTrace, ocrText, activePage }) => {
  const [msgs, setMsgs]       = useState([{ role: 'assistant', content: 'I have access to the current encounter trace and OCR text. Ask me anything about this encounter, the codes, or the chart documentation.' }]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef             = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...msgs, { role: 'user', content: text }];
    setMsgs(next);
    setInput('');
    setLoading(true);

    const system = [
      'You are a clinical HCC coding assistant. Be concise and accurate.',
      encounterTrace ? `\nEncounter: ${encounterTrace.encounter_date}, pages ${encounterTrace.start_page}–${encounterTrace.end_page}.` : '',
      encounterTrace?.coded_findings?.length
        ? `\nProposed codes: ${encounterTrace.coded_findings.map(f => `${f.icd10_code} (${f.description})`).join('; ')}.`
        : '',
      ocrText ? `\nOCR text of current page (${activePage}):\n"""\n${ocrText.slice(0, 3000)}\n"""` : '',
    ].join('');

    try {
      const res  = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system,
          messages: next.map(m => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await res.json();
      setMsgs(p => [...p, { role: 'assistant', content: data.content?.[0]?.text || 'No response.' }]);
    } catch {
      setMsgs(p => [...p, { role: 'assistant', content: 'API error. Check your configuration.' }]);
    }
    setLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px 4px' }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ marginBottom: 8, display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '90%', padding: '7px 11px', fontSize: 17, lineHeight: 1.55,
              borderRadius: m.role === 'user' ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
              background: m.role === 'user' ? C.brand : '#F3F4F6',
              color: m.role === 'user' ? '#fff' : '#111827',
              border: m.role === 'user' ? 'none' : `1px solid ${C.border}`,
              whiteSpace: 'pre-wrap',
            }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 6 }}>
            <div style={{ padding: '7px 12px', borderRadius: '12px 12px 12px 3px', background: '#F3F4F6', border: `1px solid ${C.border}`, fontSize: 17 }}>
              <Spin size="small" style={{ marginRight: 6 }} />
              <Text style={{ color: '#6B7280', fontSize: 17 }}>Thinking…</Text>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: 'flex', gap: 6, padding: '8px 10px', borderTop: `1px solid ${C.border}` }}>
        <Input
          value={input} onChange={e => setInput(e.target.value)} onPressEnter={send}
          placeholder="Ask about this encounter…"
          style={{ fontSize: 17, flex: 1 }} disabled={loading}
        />
        <Button type="primary" icon={<SendOutlined />} onClick={send}
          disabled={loading || !input.trim()} style={{ background: C.brand, borderColor: C.brand }} />
      </div>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const HCCChartChasingPage = () => {
  // data
  const [consolidatedData,   setConsolidatedData]   = useState(null);
  const [encounters,         setEncounters]          = useState([]);
  const [selectedEncId,      setSelectedEncId]       = useState(null);
  const [encounterTrace,     setEncounterTrace]      = useState(null);
  const [traceLoading,       setTraceLoading]        = useState(false);

  // review
  const [decisions,      setDecisions]      = useState({});   // findingId → 'approved'|'rejected'|'pending'
  const [finalizedCodes, setFinalizedCodes] = useState([]);   // all approved across chart
  const [addedCodes,     setAddedCodes]     = useState({});   // encounterId → [finding]

  // ui
  const [selectedFindingId, setSelectedFindingId] = useState(null);
  const [expandedIds,       setExpandedIds]       = useState(new Set());
  const [activePage,        setActivePage]        = useState(null);
  const [ocrText,           setOcrText]           = useState('');
  const [ocrLoading,        setOcrLoading]        = useState(false);
  const [addCodeVisible,    setAddCodeVisible]    = useState(false);

  // ── load OCR ──
  const loadOcr = useCallback(async (page) => {
    if (!page) return;
    setOcrLoading(true);
    setOcrText('');
    try {
      const r = await fetch(`/pages/${page}.txt`);
      if (!r.ok) throw new Error();
      setOcrText(await r.text());
    } catch {
      setOcrText(`[OCR not found: /public/pages/${page}.txt]`);
    }
    setOcrLoading(false);
  }, []);

  // ── load encounter trace ──
  const loadTrace = useCallback(async (encId, startPage) => {
    setTraceLoading(true);
    setEncounterTrace(null);
    setSelectedFindingId(null);
    setExpandedIds(new Set());
    try {
      const r = await fetch(`/data/encounter_trace_${encId}.json`);
      if (!r.ok) throw new Error();
      const t = await r.json();
      setEncounterTrace(t);
      const pg = t.start_page;
      setActivePage(pg);
      loadOcr(pg);
      if (t.coded_findings?.length) setSelectedFindingId(t.coded_findings[0].finding_id);
    } catch {
      // fallback to demo trace (only for enc 001)
      setEncounterTrace(DEMO_TRACE);
      setActivePage(DEMO_TRACE.start_page);
      loadOcr(DEMO_TRACE.start_page);
      setSelectedFindingId(DEMO_TRACE.coded_findings[0]?.finding_id || null);
    }
    setTraceLoading(false);
  }, [loadOcr]);

  // ── init ──
  useEffect(() => {
    fetch('/data/chart_codes_consolidated.json')
      .then(r => r.json())
      .then(data => {
        setConsolidatedData(data);
        setEncounters(data.encounters || []);
        const first = data.encounters?.[0];
        if (first) { setSelectedEncId(first.encounter_id); loadTrace(first.encounter_id, first.start_page); }
      })
      .catch(() => {
        setConsolidatedData(DEMO_CONSOLIDATED);
        setEncounters(DEMO_CONSOLIDATED.encounters);
        const first = DEMO_CONSOLIDATED.encounters[0];
        setSelectedEncId(first.encounter_id);
        loadTrace(first.encounter_id, first.start_page);
      });
  }, [loadTrace]);

  // ── handlers ──
  const selectEncounter = (enc) => {
    setSelectedEncId(enc.encounter_id);
    loadTrace(enc.encounter_id, enc.start_page);
  };

  const selectFinding = (f) => {
    setSelectedFindingId(f.finding_id);
    const pg = f.evidence?.[0]?.page;
    if (pg && pg !== activePage) { setActivePage(pg); loadOcr(pg); }
  };

  const toggleExpand = (id, f) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); }
      else {
        next.add(id);
        const pg = f.evidence?.[0]?.page;
        if (pg) { setActivePage(pg); loadOcr(pg); }
      }
      return next;
    });
  };

  const approve = (f) => {
    setDecisions(p => ({ ...p, [f.finding_id]: 'approved' }));
    setFinalizedCodes(p => p.find(fc => fc.finding_id === f.finding_id) ? p : [...p, { ...f, encounterId: selectedEncId, encounterDate: encounterTrace?.encounter_date }]);
    message.success({ content: `${f.icd10_code} approved`, duration: 1.5 });
  };

  const reject = (f) => {
    setDecisions(p => ({ ...p, [f.finding_id]: 'rejected' }));
    setFinalizedCodes(p => p.filter(fc => fc.finding_id !== f.finding_id));
    message.error({ content: `${f.icd10_code} rejected`, duration: 1.5 });
  };

  const handleAddCode = (vals) => {
    const nf = {
      finding_id: `coder-${Date.now()}`,
      icd10_code: vals.icd10_code,
      description: vals.description,
      source: 'coder',
      section: vals.meat_category,
      meat_category: vals.meat_category,
      support_status: 'coder_added',
      display_status: 'direct',
      evidence: vals.page_ref
        ? [{ evidence_id: 'coder', entity_text: vals.description, section: vals.meat_category, page: Number(vals.page_ref), context: { before: '', match: vals.description, after: '' } }]
        : [],
      corroboration: { llm_reasoning: vals.coder_reasoning, supporting_evidence: [] },
      rule_modifications: [],
      hcc_mapping: { mapped: false },
    };
    setAddedCodes(p => ({ ...p, [selectedEncId]: [...(p[selectedEncId] || []), nf] }));
    setDecisions(p => ({ ...p, [nf.finding_id]: 'approved' }));
    setFinalizedCodes(p => [...p, { ...nf, encounterId: selectedEncId, encounterDate: encounterTrace?.encounter_date }]);
    setAddCodeVisible(false);
    message.success(`${vals.icd10_code} added and approved`);
  };

  // ── derived ──
  const selEnc      = encounters.find(e => e.encounter_id === selectedEncId);
  const allFindings = [...(encounterTrace?.coded_findings || []), ...(addedCodes[selectedEncId] || [])];
  const selFinding  = allFindings.find(f => f.finding_id === selectedFindingId);
  const pendingCnt  = allFindings.filter(f => (decisions[f.finding_id] || 'pending') === 'pending').length;

  // ── keyboard shortcuts legend ──
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Keep a ref to latest derived values so the keydown closure is never stale
  const kbRef = useRef({});
  useEffect(() => {
    kbRef.current = { allFindings, selFinding, selectedFindingId, activePage, encounterTrace, encounters, selectedEncId };
  });

  useEffect(() => {
    const handler = (e) => {
      // Never fire when user is typing
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      // Never fire if a modal is open
      if (addCodeVisible) return;

      const {
        allFindings, selFinding, selectedFindingId,
        activePage, encounterTrace, encounters, selectedEncId,
      } = kbRef.current;

      const idx      = allFindings.findIndex(f => f.finding_id === selectedFindingId);
      const encIdx   = encounters.findIndex(e => e.encounter_id === selectedEncId);
      const key      = e.key;

      switch (key) {
        // ── Code actions ──
        case 'a':
        case 'A':
          if (selFinding) { approve(selFinding); }
          break;

        case 'r':
        case 'R':
          if (selFinding) { reject(selFinding); }
          break;

        case 'e':
        case 'E':
          if (selFinding) toggleExpand(selFinding.finding_id, selFinding);
          break;

        case 'n':
        case 'N':
          if (selFinding) { approve(selFinding); }
          // fall through to advance to next code
          /* falls through */
        case 'ArrowDown':
        case 'j':
        case 'J': {
          e.preventDefault();
          const next = allFindings[idx + 1];
          if (next) selectFinding(next);
          break;
        }

        case 'ArrowUp':
        case 'k':
        case 'K': {
          e.preventDefault();
          const prev = allFindings[idx - 1];
          if (prev) selectFinding(prev);
          break;
        }

        // ── Page navigation ──
        case 'ArrowRight':
        case ']': {
          e.preventDefault();
          const max = encounterTrace?.end_page || 999;
          const np  = Math.min((activePage || 1) + 1, max);
          setActivePage(np); loadOcr(np);
          break;
        }

        case 'ArrowLeft':
        case '[': {
          e.preventDefault();
          const min = encounterTrace?.start_page || 1;
          const pp  = Math.max((activePage || 1) - 1, min);
          setActivePage(pp); loadOcr(pp);
          break;
        }

        // ── Encounter navigation ──
        case '}':
        case '.': {
          const nextEnc = encounters[encIdx + 1];
          if (nextEnc) selectEncounter(nextEnc);
          break;
        }

        case '{':
        case ',': {
          const prevEnc = encounters[encIdx - 1];
          if (prevEnc) selectEncounter(prevEnc);
          break;
        }

        // ── Add missed code ──
        case '+':
        case '=':
          setAddCodeVisible(true);
          break;

        // ── Help ──
        case '?':
          setShowShortcuts(s => !s);
          break;

        case 'Escape':
          setShowShortcuts(false);
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCodeVisible]);

  // ── resizable panel widths ──
  const [encW,   setEncW]   = useState(210);
  const [codesW, setCodesW] = useState(330);
  const [finalW, setFinalW] = useState(280);
  const [ocrW,   setOcrW]   = useState(270);

  // Generic drag-resize
  const startDrag = useCallback((e, setter, getBase) => {
    e.preventDefault();
    const startX = e.clientX;
    const base   = getBase();
    const onMove = (me) => setter(Math.max(140, base + (me.clientX - startX)));
    const onUp   = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); document.body.style.cursor = ''; };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // Resize handle between panels
  const ResizeHandle = ({ onDrag }) => (
    <div
      onMouseDown={onDrag}
      title="Drag to resize"
      style={{
        width: 6, flexShrink: 0, cursor: 'col-resize',
        background: C.border,
        position: 'relative', zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = C.brand + '55'}
      onMouseLeave={e => e.currentTarget.style.background = C.border}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, pointerEvents: 'none' }}>
        {[0,1,2,3,4].map(i => (
          <div key={i} style={{ width: 2, height: 2, borderRadius: '50%', background: '#9CA3AF' }} />
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', fontFamily: "'Inter', 'Segoe UI', sans-serif", background: '#fff' }}>

      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <div style={{
        height: C.HEADER_H, flexShrink: 0,
        background: `linear-gradient(90deg, ${C.brandDark} 0%, ${C.brand} 100%)`,
        display: 'flex', alignItems: 'center', padding: '0 14px', gap: 10,
        boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
      }}>
        <MedicineBoxOutlined style={{ color: '#D4E7F0', fontSize: 21 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ color: '#fff', fontWeight: 700, fontSize: 17, display: 'block', lineHeight: '19px' }}>
            {consolidatedData?.chart_id || 'Chart Review'}
          </Text>
          <Text style={{ color: '#D4E7F0', fontSize: 16 }}>
            {encounters.length} encounters · {consolidatedData?.total_codes || 0} AI-proposed codes
          </Text>
        </div>
        {/* stats */}
        {[
          { val: finalizedCodes.length,  label: 'Approved', col: '#86EFAC' },
          { val: pendingCnt,             label: 'Pending',  col: '#FDE047' },
          { val: allFindings.filter(f => decisions[f.finding_id] === 'rejected').length, label: 'Rejected', col: '#FCA5A5' },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center', minWidth: 44 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.col, lineHeight: '22px' }}>{s.val}</div>
            <div style={{ fontSize: 16, color: '#D4E7F0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
          </div>
        ))}
        <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
        <Button icon={<SaveOutlined />} style={{ background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.28)', fontSize: 17 }}>
          Save Draft
        </Button>
        <Button
          onClick={() => setShowShortcuts(true)}
          title="Keyboard shortcuts"
          style={{ background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.28)', fontSize: 17, fontWeight: 700, minWidth: 38, padding: '0 10px' }}
        >
          ?
        </Button>
        <Button type="primary" icon={<CheckCircleFilled />} style={{ background: '#15803D', borderColor: '#15803D', fontWeight: 700, fontSize: 17 }}>
          Complete
        </Button>
      </div>

      {/* ══ BODY ════════════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', userSelect: 'none' }}>

        {/* ── P1 Encounter List ─────────────────────────────────────────── */}
        <div style={{ width: encW, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#FAFAFA', overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px 6px', borderBottom: `1px solid ${C.border}`, background: '#F0EBFF', flexShrink: 0 }}>
            <Text style={{ fontSize: 17, fontWeight: 700, color: C.brand, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Encounters</Text>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {encounters.map((enc, idx) => {
              const isActive  = enc.encounter_id === selectedEncId;
              const approved  = finalizedCodes.filter(fc => fc.encounterId === enc.encounter_id).length;
              return (
                <div key={enc.encounter_id} onClick={() => selectEncounter(enc)} style={{
                  padding: '9px 10px', cursor: 'pointer',
                  borderBottom: `1px solid ${C.border}`,
                  borderLeft: `3px solid ${isActive ? C.brand : 'transparent'}`,
                  background: isActive ? C.brandLight : 'transparent',
                  transition: 'background 0.1s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: 17, fontWeight: 700, color: isActive ? C.brand : '#9CA3AF' }}>
                      ENC {String(idx + 1).padStart(2, '0')}
                    </Text>
                    {approved > 0 && <Badge count={approved} style={{ background: C.gospel, fontSize: 16, height: 17, lineHeight: '17px', minWidth: 17 }} />}
                  </div>
                  <Text style={{ fontSize: 17, fontWeight: isActive ? 700 : 400, color: '#111827', display: 'block', marginTop: 2 }}>{enc.encounter_date}</Text>
                  <Text style={{ fontSize: 17, color: '#6B7280', display: 'block', marginTop: 1 }}>pp. {enc.start_page}–{enc.end_page}</Text>
                  <div style={{ marginTop: 4 }}>
                    <Pill label={`${enc.coded_findings_count} codes`} color={isActive ? C.brand : '#6B7280'} bg={isActive ? C.brandLight : '#F3F4F6'} border={isActive ? '#DDD6FE' : '#E5E7EB'} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <ResizeHandle onDrag={(e) => startDrag(e, setEncW, () => encW)} />

        {/* ── P2 Proposed Codes ─────────────────────────────────────────── */}
        <div style={{ width: codesW, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden' }}>
          <div style={{ padding: '6px 10px', borderBottom: `1px solid ${C.border}`, background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <Text style={{ fontSize: 17, fontWeight: 700, color: '#374151', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Proposed Codes</Text>
              {selEnc && <Text style={{ fontSize: 17, color: '#9CA3AF', display: 'block' }}>{selEnc.encounter_date} · {allFindings.length} codes</Text>}
            </div>
            <Text style={{ fontSize: 17, color: C.gospel }}>{allFindings.filter(f => decisions[f.finding_id] === 'approved').length} approved</Text>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {traceLoading ? (
              <div style={{ padding: 40, textAlign: 'center' }}><Spin /><div style={{ marginTop: 8, color: '#9CA3AF', fontSize: 17 }}>Loading…</div></div>
            ) : allFindings.length === 0 ? (
              <Empty description="No codes" style={{ marginTop: 40 }} />
            ) : allFindings.map(f => (
              <CodeRow
                key={f.finding_id}
                finding={f}
                isSelected={selectedFindingId === f.finding_id}
                isExpanded={expandedIds.has(f.finding_id)}
                decision={decisions[f.finding_id] || 'pending'}
                onSelect={() => selectFinding(f)}
                onToggleExpand={() => toggleExpand(f.finding_id, f)}
                onApprove={() => approve(f)}
                onReject={() => reject(f)}
                onPageJump={pg => { setActivePage(pg); loadOcr(pg); }}
              />
            ))}
          </div>
          <div style={{ padding: '6px 10px 8px', borderTop: `1px solid ${C.border}`, background: '#FAFAFA', flexShrink: 0 }}>
            {/* keyboard hint strip */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 7, flexWrap: 'wrap' }}>
              {[
                { key: 'A', label: 'Approve' },
                { key: 'R', label: 'Reject' },
                { key: 'N', label: 'Next' },
                { key: 'E', label: 'Expand' },
                { key: 'J/K', label: '↑↓' },
              ].map(({ key, label }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <kbd style={{
                    padding: '1px 6px', borderRadius: 4,
                    background: '#F3F4F6', border: '1px solid #D1D5DB',
                    borderBottom: '2px solid #9CA3AF',
                    fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#374151',
                  }}>{key}</kbd>
                  <span style={{ fontSize: 12, color: '#9CA3AF' }}>{label}</span>
                </div>
              ))}
            </div>
            <Button block icon={<PlusOutlined />} onClick={() => setAddCodeVisible(true)}
              style={{ borderStyle: 'dashed', color: C.brand, borderColor: C.brand, fontSize: 17 }}>
              Add Missed Code
            </Button>
          </div>
        </div>

        <ResizeHandle onDrag={(e) => startDrag(e, setCodesW, () => codesW)} />

        {/* ── P3 Chart Viewer ──────────────────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', background: '#F8F9FA', overflow: 'hidden' }}>
          {/* page nav bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#FFFFFF', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
            <FileTextOutlined style={{ color: C.brand, fontSize: 17 }} />
            <Text style={{ color: '#374151', fontSize: 16, fontWeight: 600 }}>Chart Page</Text>
            {/* page stepper */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#F3F4F6', borderRadius: 5, padding: '2px 8px', border: `1px solid ${C.border}` }}>
              <button onClick={() => { const p = Math.max((activePage||1)-1, encounterTrace?.start_page||1); setActivePage(p); loadOcr(p); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.brand, fontSize: 17, padding: '0 2px' }}>‹</button>
              <Text style={{ color: '#111827', fontSize: 17, fontWeight: 700, minWidth: 22, textAlign: 'center' }}>{activePage ?? '—'}</Text>
              <button onClick={() => { const p = Math.min((activePage||1)+1, encounterTrace?.end_page||999); setActivePage(p); loadOcr(p); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.brand, fontSize: 17, padding: '0 2px' }}>›</button>
            </div>
            {encounterTrace && <Text style={{ color: '#9CA3AF', fontSize: 17 }}>enc pages {encounterTrace.start_page}–{encounterTrace.end_page}</Text>}
            {/* evidence page jump buttons from selected finding */}
            {selFinding?.evidence?.map(ev => ev.page).filter(Boolean).map(pg => (
              <button key={pg} onClick={() => { setActivePage(pg); loadOcr(pg); }}
                style={{ background: activePage === pg ? C.brand : '#EDE9FE', border: `1px solid ${C.brand}44`, borderRadius: 4, color: activePage === pg ? '#fff' : C.brand, fontSize: 17, fontWeight: 600, cursor: 'pointer', padding: '2px 9px' }}>
                p.{pg}
              </button>
            ))}
            {selFinding?.corroboration?.supporting_evidence?.map(ev => ev.page).filter(Boolean).filter((pg, i, a) => a.indexOf(pg) === i).map(pg => (
              <button key={`s${pg}`} onClick={() => { setActivePage(pg); loadOcr(pg); }}
                style={{ background: '#F0FDF4', border: `1px solid ${C.gospel}44`, borderRadius: 4, color: C.gospel, fontSize: 17, fontWeight: 600, cursor: 'pointer', padding: '2px 9px' }}>
                supp p.{pg}
              </button>
            ))}
          </div>

          {/* PDF + OCR sidebar */}
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* PDF iframe */}
            <div style={{ flex: 1, minWidth: 0, background: '#fff', overflow: 'hidden' }}>
              {activePage ? (
                <iframe
                  key={activePage}
                  src={`/pages/${activePage}.pdf`}
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block', background: '#fff' }}
                  title={`Page ${activePage}`}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#fff' }}>
                  <Text style={{ color: '#9CA3AF' }}>Select a code to view the chart</Text>
                </div>
              )}
            </div>

            <ResizeHandle onDrag={(e) => startDrag(e, setOcrW, () => ocrW)} />

            {/* OCR evidence sidebar */}
            <div style={{ width: ocrW, flexShrink: 0, background: '#FFFFFF', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '6px 10px', borderBottom: `1px solid ${C.border}`, background: '#F5F3FF', flexShrink: 0 }}>
                <Text style={{ fontSize: 17, fontWeight: 700, color: C.brand, letterSpacing: '0.08em', textTransform: 'uppercase' }}>OCR Evidence</Text>
              </div>
              {selFinding && (
                <div style={{ padding: '8px 10px 6px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
                  <Text style={{ fontSize: 17, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700 }}>Selected: {selFinding.icd10_code}</Text>
                  {selFinding.evidence?.map((ev, i) => <EvidenceSnippet key={i} context={ev.context} section={ev.section} />)}
                  {selFinding.corroboration?.supporting_evidence?.slice(0, 2).map((ev, i) => <EvidenceSnippet key={`c${i}`} context={ev.context} section={ev.section} />)}
                </div>
              )}
              <div style={{ flex: 1, overflowY: 'auto', padding: '6px 10px' }}>
                <Text style={{ fontSize: 17, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700 }}>Raw OCR — p.{activePage}</Text>
                {ocrLoading
                  ? <Spin size="small" style={{ display: 'block', marginTop: 10 }} />
                  : <pre style={{ fontSize: 17, color: '#374151', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 6, fontFamily: 'monospace', background: '#FAFAFA', border: `1px solid ${C.border}`, borderRadius: 5, padding: '8px 10px' }}>{ocrText || '(no OCR for this page)'}</pre>
                }
              </div>
            </div>
          </div>
        </div>

        <ResizeHandle onDrag={(e) => startDrag(e, setFinalW, () => finalW)} />

        {/* ── P4 Finalized Codes + Chat ─────────────────────────────────── */}
        <div style={{ width: finalW, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#FAFAFA', overflow: 'hidden' }}>
          {/* finalized codes */}
          <div style={{ borderBottom: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', flex: '0 0 auto', maxHeight: '52%', minHeight: 100, overflow: 'hidden' }}>
            <div style={{ padding: '6px 10px', background: '#F0FDF4', borderBottom: `1px solid ${C.gospelBdr}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Text style={{ fontSize: 17, fontWeight: 700, color: C.gospel, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Finalized Codes</Text>
              <Badge count={finalizedCodes.length} style={{ background: C.gospel }} showZero />
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {finalizedCodes.length === 0
                ? <div style={{ padding: '18px 10px', textAlign: 'center' }}><Text style={{ color: '#9CA3AF', fontSize: 17 }}>No codes approved yet</Text></div>
                : finalizedCodes.map((fc, i) => {
                    const badges = hccBadges(fc.hcc_mapping);
                    return (
                      <div key={fc.finding_id} style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? '#fff' : '#F9FAFB' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <CheckCircleFilled style={{ color: C.gospel, fontSize: 16, flexShrink: 0 }} />
                          <Text style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 17, color: '#111827' }}>{fc.icd10_code}</Text>
                          {fc.source === 'coder' && <Pill label="Coder" color="#92400E" bg="#FFFBEB" border="#FDE68A" />}
                        </div>
                        <Text style={{ fontSize: 16, color: '#374151', display: 'block', marginTop: 1, lineHeight: '15px' }}>{fc.description}</Text>
                        <div style={{ display: 'flex', gap: 3, marginTop: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                          {badges.map(b => <span key={b.label} style={{ fontSize: 16, padding: '1px 5px', borderRadius: 3, background: b.color, color: '#fff', fontWeight: 700 }}>{b.label}</span>)}
                          <Text style={{ fontSize: 17, color: '#9CA3AF' }}>{fc.encounterDate}</Text>
                        </div>
                      </div>
                    );
                  })
              }
            </div>
          </div>

          {/* chat */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '6px 10px', borderBottom: `1px solid ${C.border}`, background: '#F5F3FF', flexShrink: 0 }}>
              <Text style={{ fontSize: 17, fontWeight: 700, color: C.brand, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Chart Assistant</Text>
              <Text style={{ fontSize: 17, color: '#9CA3AF', display: 'block' }}>Context-aware · current encounter + page</Text>
            </div>
            <ChatPanel encounterTrace={encounterTrace} ocrText={ocrText} activePage={activePage} />
          </div>
        </div>
      </div>

      {/* ── Keyboard Shortcuts Legend Modal ── */}
      <Modal
        open={showShortcuts}
        onCancel={() => setShowShortcuts(false)}
        footer={null}
        width={560}
        title={
          <span style={{ color: C.brand, fontWeight: 700, fontSize: 17 }}>
            ⌨️ Keyboard Shortcuts
          </span>
        }
      >
        {[
          { group: 'Code Actions', rows: [
            { keys: ['A'], desc: 'Approve selected code' },
            { keys: ['R'], desc: 'Reject selected code' },
            { keys: ['N'], desc: 'Approve & advance to next code' },
            { keys: ['E'], desc: 'Expand / collapse provenance' },
            { keys: ['+', '='], desc: 'Add missed code' },
          ]},
          { group: 'Navigate Codes', rows: [
            { keys: ['J', '↓'], desc: 'Next code' },
            { keys: ['K', '↑'], desc: 'Previous code' },
          ]},
          { group: 'Navigate Chart Pages', rows: [
            { keys: [']', '→'], desc: 'Next page' },
            { keys: ['[', '←'], desc: 'Previous page' },
          ]},
          { group: 'Navigate Encounters', rows: [
            { keys: ['.', '}'], desc: 'Next encounter' },
            { keys: [',', '{'], desc: 'Previous encounter' },
          ]},
          { group: 'General', rows: [
            { keys: ['?'], desc: 'Show / hide this help' },
            { keys: ['Esc'], desc: 'Close dialogs' },
          ]},
        ].map(({ group, rows }) => (
          <div key={group} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
              {group}
            </div>
            {rows.map(({ keys, desc }) => (
              <div key={desc} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
                <Text style={{ fontSize: 16, color: '#374151' }}>{desc}</Text>
                <div style={{ display: 'flex', gap: 4 }}>
                  {keys.map(k => (
                    <kbd key={k} style={{
                      display: 'inline-block',
                      padding: '2px 8px', borderRadius: 5,
                      background: '#F3F4F6', border: '1px solid #D1D5DB',
                      borderBottom: '3px solid #9CA3AF',
                      fontFamily: 'monospace', fontSize: 15, fontWeight: 700,
                      color: '#111827', lineHeight: '22px',
                    }}>{k}</kbd>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
        <div style={{ marginTop: 8, padding: '8px 12px', background: '#D4E7F0', borderRadius: 6, fontSize: 14, color: '#6B7280' }}>
          💡 Shortcuts are disabled when typing in text fields or modals.
        </div>
      </Modal>

      {/* ── Add Code Modal ── */}
      <AddCodeModal
        visible={addCodeVisible}
        encounterDate={selEnc?.encounter_date}
        onAdd={handleAddCode}
        onClose={() => setAddCodeVisible(false)}
      />
    </div>
  );
};

// ─── Demo data (fallback) ────────────────────────────────────────────────────
const DEMO_CONSOLIDATED = {
  chart_id: 'M_1dzWbdqwxM',
  consolidated_at: '2026-04-24T14:57:10.842196',
  total_codes: 23,
  encounters: [
    { encounter_id: 'encounter_001_p0009_p0017', encounter_number: 1, encounter_date: '2024-12-10', start_page: 9,  end_page: 17, chart_type: 'professional', coded_findings_count: 8 },
    { encounter_id: 'encounter_002_p0018_p0024', encounter_number: 2, encounter_date: '2024-10-31', start_page: 18, end_page: 24, chart_type: 'professional', coded_findings_count: 8 },
    { encounter_id: 'encounter_003_p0025_p0033', encounter_number: 3, encounter_date: '2024-10-10', start_page: 25, end_page: 33, chart_type: 'professional', coded_findings_count: 5 },
    { encounter_id: 'encounter_004_p0034_p0040', encounter_number: 4, encounter_date: '2024-10-03', start_page: 34, end_page: 40, chart_type: 'professional', coded_findings_count: 6 },
    { encounter_id: 'encounter_005_p0041_p0047', encounter_number: 5, encounter_date: '2024-09-05', start_page: 41, end_page: 47, chart_type: 'professional', coded_findings_count: 6 },
    { encounter_id: 'encounter_006_p0048_p0056', encounter_number: 6, encounter_date: '2024-08-29', start_page: 48, end_page: 56, chart_type: 'professional', coded_findings_count: 5 },
    { encounter_id: 'encounter_007_p0057_p0062', encounter_number: 7, encounter_date: '2024-04-09', start_page: 57, end_page: 62, chart_type: 'professional', coded_findings_count: 6 },
    { encounter_id: 'encounter_008_p0063_p0068', encounter_number: 8, encounter_date: '2024-03-19', start_page: 63, end_page: 68, chart_type: 'professional', coded_findings_count: 6 },
    { encounter_id: 'encounter_009_p0069_p0074', encounter_number: 9, encounter_date: '2024-01-09', start_page: 69, end_page: 74, chart_type: 'professional', coded_findings_count: 5 },
  ],
};

const DEMO_TRACE = {
  encounter_id: 'encounter_001_p0009_p0017',
  encounter_number: 1, start_page: 9, end_page: 17,
  encounter_date: '2024-12-10', chart_type: 'professional',
  coded_findings: [
    {
      finding_id: 'b4e33533', icd10_code: 'E11.9',
      description: 'Type 2 diabetes mellitus without complications',
      source: 'physician', section: 'assessment_plan', meat_category: 'A&P',
      support_status: 'gospel', display_status: 'direct',
      evidence: [{ evidence_id: 'b3c508a5', entity_text: 'Type 2 diabetes mellitus without complications', section: 'assessment_plan', page: 15, char_span: [596, 649], context: { before: 'nic kidney disease stage 3\n N18.32: Chronic kidney disease, stage 3b\n\n 3. Type 2 diabetes mellitus\n ', match: 'E11.9: Type 2 diabetes mellitus without complications', after: '\n\n 4. Sleep apnea\n G47.30: Sleep apnea, unspecified\n\n 5. Mixed hyperlipidemia\n E78.2: Mixed hyperlip' }, extracted_by: 'unified_ner_extraction' }],
      corroboration: { status: 'gospel', supporting_evidence: [], llm_reasoning: 'Gospel section: assessment_plan', evaluated_at: '2026-04-24T18:53:20.675650+00:00' },
      rule_modifications: [],
      hcc_mapping: { mapped: true, hcc_v24: 'Yes', hcc_v28: 'Yes', rxhcc_v08: 'Yes', rxhcc_excluded: false },
    },
    {
      finding_id: 'f397bd7a', icd10_code: 'G47.30',
      description: 'Sleep apnea, unspecified',
      source: 'llm', section: 'assessment_plan', meat_category: 'A&P',
      support_status: 'gospel', display_status: 'direct',
      evidence: [{ evidence_id: 'ed7d4097', entity_text: 'Sleep apnea, unspecified', section: 'assessment_plan', page: 14, char_span: [1414, 1425], context: { before: ' stage 3b\n\n 3. Type 2 diabetes mellitus\n E11.9: Type 2 diabetes mellitus without complications\n\n 4. ', match: 'Sleep apnea', after: '\n G47.30: Sleep apnea, unspecified\n\n 5. Mixed hyperlipidemia\n E78.2: Mixed hyperlipidemia\n\n 6. Hyper' }, extracted_by: 'unified_ner_extraction' }],
      corroboration: { status: 'gospel', supporting_evidence: [], llm_reasoning: 'Gospel section: assessment_plan', evaluated_at: '2026-04-24T18:53:20.675706+00:00' },
      rule_modifications: [],
      hcc_mapping: { mapped: false, hcc_v24: null, hcc_v28: null, rxhcc_v08: null, rxhcc_excluded: false },
    },
    {
      finding_id: 'fa310306', icd10_code: 'E78.2',
      description: 'Mixed hyperlipidemia',
      source: 'llm', section: 'assessment_plan', meat_category: 'A&P',
      support_status: 'gospel', display_status: 'direct',
      evidence: [{ evidence_id: '72dda831', entity_text: 'Mixed hyperlipidemia', section: 'assessment_plan', page: 15, char_span: [706, 726], context: { before: 'e 2 diabetes mellitus without complications\n\n 4. Sleep apnea\n G47.30: Sleep apnea, unspecified\n\n 5. ', match: 'Mixed hyperlipidemia', after: '\n E78.2: Mixed hyperlipidemia\n\n 6. Hypertensive disorder\n I10: Essential (primary) hypertension\n\n 7.' }, extracted_by: 'unified_ner_extraction' }],
      corroboration: { status: 'gospel', supporting_evidence: [], llm_reasoning: 'Gospel section: assessment_plan', evaluated_at: '2026-04-24T18:53:20.675714+00:00' },
      rule_modifications: [],
      hcc_mapping: { mapped: true, hcc_v24: 'No', hcc_v28: 'No', rxhcc_v08: 'Yes', rxhcc_excluded: false },
    },
    {
      finding_id: 'eb97b3a7', icd10_code: 'I10',
      description: 'Essential (primary) hypertension',
      source: 'llm', section: 'assessment_plan', meat_category: 'A&P',
      support_status: 'gospel', display_status: 'direct',
      evidence: [{ evidence_id: '94704f8c', entity_text: 'Essential (primary) hypertension', section: 'assessment_plan', page: 15, char_span: [761, 782], context: { before: 'apnea\n G47.30: Sleep apnea, unspecified\n\n 5. Mixed hyperlipidemia\n E78.2: Mixed hyperlipidemia\n\n 6. ', match: 'Hypertensive disorder', after: '\n I10: Essential (primary) hypertension\n\n 7. Gastroesophageal reflux disease\n K21.9: Gastro-esophage' }, extracted_by: 'unified_ner_extraction' }],
      corroboration: { status: 'gospel', supporting_evidence: [], llm_reasoning: 'Gospel section: assessment_plan', evaluated_at: '2026-04-24T18:53:20.675721+00:00' },
      rule_modifications: [],
      hcc_mapping: { mapped: true, hcc_v24: 'No', hcc_v28: 'No', rxhcc_v08: 'Yes', rxhcc_excluded: false },
    },
    {
      finding_id: 'cb98d32e', icd10_code: 'K21.9',
      description: 'Gastro-esophageal reflux disease without esophagitis',
      source: 'llm', section: 'assessment_plan', meat_category: 'A&P',
      support_status: 'gospel', display_status: 'direct',
      evidence: [{ evidence_id: '95bd5527', entity_text: 'Gastro-esophageal reflux disease without esophagitis', section: 'assessment_plan', page: 15, char_span: [827, 858], context: { before: ' E78.2: Mixed hyperlipidemia\n\n 6. Hypertensive disorder\n I10: Essential (primary) hypertension\n\n 7. ', match: 'Gastroesophageal reflux disease', after: '\n K21.9: Gastro-esophageal reflux disease without esophagitis\n\n Return to Office\n Anthony Stugan, MD' }, extracted_by: 'unified_ner_extraction' }],
      corroboration: { status: 'gospel', supporting_evidence: [], llm_reasoning: 'Gospel section: assessment_plan', evaluated_at: '2026-04-24T18:53:20.675748+00:00' },
      rule_modifications: [],
      hcc_mapping: { mapped: false, hcc_v24: null, hcc_v28: null, rxhcc_v08: null, rxhcc_excluded: false },
    },
    {
      finding_id: '8d28d945', icd10_code: 'E11.42',
      description: 'Polyneuropathy in diseases classified elsewhere',
      source: 'physician', section: 'assessment_plan', meat_category: 'A&P',
      support_status: 'gospel', display_status: 'direct',
      evidence: [{ evidence_id: '19accd15', entity_text: 'Polyneuropathy in diseases classified elsewhere', section: 'assessment_plan', page: 15, char_span: [422, 486], context: { before: 'sting labs at that time to include A1c CBC CMP lipd panel, urine microalbumin creatinine ratio\n\n 1. ', match: 'Neuropathy\n G63: Polyneuropathy in diseases classified elsewhere', after: '\n\n 2. Chronic kidney disease stage 3\n N18.32: Chronic kidney disease, stage 3b\n\n 3. Type 2 diabetes ' }, extracted_by: 'unified_ner_extraction' }],
      corroboration: { status: 'gospel', supporting_evidence: [], llm_reasoning: 'Gospel section: assessment_plan', evaluated_at: '2026-04-24T18:53:20.675763+00:00' },
      rule_modifications: [{ modification_id: 'b68a6d4d', rule_engine: 'unstructured', rule_namespace: 'ner_code_corrections', rule_name: 'NER_CORR_NEUROPATHY_DM_T2_001', action: 'replaced', before_code: 'G63', before_description: 'Polyneuropathy in diseases classified elsewhere', after_code: 'E11.42', after_description: 'Polyneuropathy in diseases classified elsewhere', rule_reasoning: 'T2DM co-documented — use diabetic polyneuropathy combination code', applied_at: '2026-04-24T18:53:20.674262+00:00' }],
      hcc_mapping: { mapped: true, hcc_v24: 'Yes', hcc_v28: 'Yes', rxhcc_v08: 'Yes', rxhcc_excluded: false },
    },
    {
      finding_id: '0f3c8355', icd10_code: 'N18.32',
      description: 'Chronic kidney disease, stage 3b',
      source: 'llm', section: 'problem_list', meat_category: 'PL with DX Support',
      support_status: 'supported', display_status: 'indirect-supported',
      evidence: [{ evidence_id: 'fce32691', entity_text: 'Chronic kidney disease, stage 3b', section: 'problem_list', page: 12, char_span: [0, 31], context: { before: '', match: 'Chronic kidney disease stage 3b', after: '' }, extracted_by: 'unified_ner_extraction' }],
      corroboration: { status: 'supported', supporting_evidence: [{ evidence_id: 'd118ec7f', evidence_type: 'medication', text: 'amlodipine 10mg', page: 9, char_span: [0, 66], context: { before: '', match: 'amLODIPine 10 mg tablet 10/30/24 filled Take 1 tablet(s) every day', after: '' }, section: 'medications' }, { evidence_id: '2e4573d9', evidence_type: 'medication', text: 'benazepril 20mg', page: 9, char_span: [0, 73], context: { before: '', match: 'benazepriL 20 mg tablet 10/30/24 filled TAKE 1 TABLET BY MOUTH ONCE DAILY', after: '' }, section: 'medications' }, { evidence_id: '43984128', evidence_type: 'medication', text: 'torsemide 10mg', page: 9, char_span: [0, 50], context: { before: '', match: 'torsemide 10 mg tablet TAKE 1 TABLET BY MOUTH DAILY', after: '' }, section: 'medications' }], llm_reasoning: 'Medications such as benazepril and diuretics are commonly used to manage chronic kidney disease.', evaluated_at: '2026-04-24T18:53:20.675850+00:00' },
      rule_modifications: [],
      hcc_mapping: { mapped: true, hcc_v24: 'Yes', hcc_v28: 'Yes', rxhcc_v08: 'No', rxhcc_excluded: false },
    },
    {
      finding_id: 'z68-42-enc1', icd10_code: 'Z68.42',
      description: 'Body mass index (BMI) 45.0-49.9, adult',
      source: 'llm', section: 'other', meat_category: 'Other with DX Support',
      support_status: 'supported', display_status: 'indirect-supported',
      evidence: [],
      corroboration: { status: 'supported', supporting_evidence: [], llm_reasoning: '[structured_rule] CORR_BMI_Z6842_001: BMI 45.0-49.9 documented in chart vitals — corroborates Z68.42', evaluated_at: '2026-04-24T18:53:22.710571+00:00' },
      rule_modifications: [{ modification_id: '13df5437', rule_engine: 'structured', rule_namespace: 'hcc_corroboration', rule_name: 'CORR_BMI_Z6842_001', action: 'annotated', before_code: '', before_description: '', after_code: 'Z68.42', after_description: 'Body mass index (BMI) 45.0-49.9, adult', rule_reasoning: 'BMI 45.0-49.9 documented in chart vitals — corroborates Z68.42', applied_at: '2026-04-24T18:53:22.710992+00:00' }],
      hcc_mapping: { mapped: true, hcc_v24: 'Yes', hcc_v28: 'Yes', rxhcc_v08: 'No', rxhcc_excluded: false },
    },
  ],
};

export default HCCChartChasingPage;